import { useAuth } from "@/src/contexts/auth.context";
import {
  LeaderboardStandings,
  toLeaderboardView,
} from "@/src/models/leaderboard.model";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { fetchStandings, leaderboardEnabled, recordShare } from "./leaderboard-functions";

export const LEADERBOARD_PAGE_SIZE = 20;

/** Every key is scoped to the active organization, so a club switch never shows the other club's board. */
export const LEADERBOARD_QUERY_KEYS = {
  base: ["leaderboard"] as const,
  org: (orgId: string) => ["leaderboard", orgId] as const,
  summary: (orgId: string) => ["leaderboard", orgId, "summary"] as const,
  pages: (orgId: string, period: string, pageSize: number, viewerId: string) =>
    ["leaderboard", orgId, "pages", period, pageSize, viewerId] as const,
};

/** The widget and full board share one first-page request/cache entry. The
 * server ranks the entire board before slicing, so requesting 20 rather than 3
 * avoids another Function invocation without adding another scoring operation.
 */
export const useLeaderboardSummaryQuery = (pageSize = 3) => {
  const query = useLeaderboardPagesQuery();
  return {
    ...query,
    data: query.data ? { ...query.data, entries: query.data.entries.slice(0, pageSize) } : undefined,
  };
};

/** Full board, paged ("load more"); `period` defaults to the current month on the server. */
export const useLeaderboardPagesQuery = (period = "current", pageSize = LEADERBOARD_PAGE_SIZE) => {
  const { orgId, user } = useAuth();
  return useInfiniteQuery({
    queryKey: LEADERBOARD_QUERY_KEYS.pages(orgId ?? "", period, pageSize, user?.itemId ?? ""),
    queryFn: ({ pageParam }) =>
      fetchStandings({ pageNo: pageParam, pageSize, ...(period === "current" ? {} : { period }) }),
    initialPageParam: 1,
    getNextPageParam: (last: LeaderboardStandings) => (last.hasNextPage ? last.pageNo + 1 : undefined),
    enabled: leaderboardEnabled && !!orgId && !!user?.itemId,
    staleTime: 1000 * 60,
    select: (data) => toLeaderboardView(data.pages),
  });
};

/** Reports a post share; the server decides whether it earns points (once per user and post). */
export const useRecordShare = () => {
  const queryClient = useQueryClient();
  const { orgId } = useAuth();
  return useMutation({
    mutationFn: (postId: string) => recordShare(postId),
    onSuccess: (result) => {
      if (result?.awarded && orgId)
        queryClient.invalidateQueries({ queryKey: LEADERBOARD_QUERY_KEYS.org(orgId) });
    },
  });
};
